/**
 * API-Vertrag zwischen Server und Web: Request-Schemas (Zod) und Response-Typen.
 */
import { z } from "zod";
import { hexColorSchema, iconSchema, idSchema, nameSchema, secsPerPageSchema } from "./common.js";
import { jumpMarkersSchema, type JumpMarker } from "./jump-markers.js";
import type { StrokeDocument } from "./strokes.js";
import type { UserSettings } from "./settings.js";

// ---------------------------------------------------------------------------
// Fehler
// ---------------------------------------------------------------------------

export interface ApiErrorBody {
  error: string;
  message: string;
  details?: unknown;
}

// ---------------------------------------------------------------------------
// Auth & Nutzer
// ---------------------------------------------------------------------------

export const PASSWORD_MIN_LENGTH = 8;
export const passwordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(256);
export const emailSchema = z
  .string()
  .trim()
  .max(254)
  .transform((value) => value.toLowerCase().normalize("NFC"))
  .pipe(z.email());

export const roleSchema = z.enum(["admin", "user"]);
export type Role = z.infer<typeof roleSchema>;

export interface UserDto {
  id: string;
  email: string;
  displayName: string;
  role: Role;
}

export const loginRequestSchema = z.object({
  email: z.string().trim().min(1).max(254),
  password: z.string().min(1).max(256),
});

export interface SessionResponse {
  user: UserDto;
  csrfToken: string;
}

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: passwordSchema,
});

// ---------------------------------------------------------------------------
// Dokumente
// ---------------------------------------------------------------------------

export type AccessRole = "owner" | "shared";

export interface DocumentDto {
  id: string;
  title: string;
  fileName: string;
  sizeBytes: number;
  mtimeMs: number;
  pageCount: number | null;
  createdAt: number;
  accessRole: AccessRole;
  owner: { id: string; displayName: string };
  /** Anzahl Nutzer, mit denen geteilt wurde (nur für Besitzer > 0). */
  shareCount: number;
  favorite: boolean;
  categoryIds: string[];
  secsPerPage: number | null;
  lastOpenedAt: number | null;
}

export interface DocumentListResponse {
  documents: DocumentDto[];
}

export const uploadMetaSchema = z.object({
  favorite: z.boolean().optional(),
  categoryIds: z.array(idSchema).max(50).optional(),
  playlistIds: z.array(idSchema).max(50).optional(),
  secsPerPage: secsPerPageSchema.optional(),
});
export type UploadMeta = z.infer<typeof uploadMetaSchema>;

export interface DocumentPrefsDto {
  secsPerPage: number | null;
  jumpMarkers: JumpMarker[];
}

export const patchDocumentPrefsSchema = z.object({
  secsPerPage: secsPerPageSchema.nullable().optional(),
  jumpMarkers: jumpMarkersSchema.optional(),
});
export type PatchDocumentPrefs = z.infer<typeof patchDocumentPrefsSchema>;

export const putDocumentCategoriesSchema = z.object({ categoryIds: z.array(idSchema).max(50) });
export const putDocumentPlaylistsSchema = z.object({ playlistIds: z.array(idSchema).max(100) });

// ---------------------------------------------------------------------------
// Teilen
// ---------------------------------------------------------------------------

export interface ShareTargetDto {
  id: string;
  displayName: string;
}

export interface DocumentSharesDto {
  sharedWith: ShareTargetDto[];
}

export const putSharesSchema = z.object({ userIds: z.array(idSchema).max(100) });

// ---------------------------------------------------------------------------
// Kategorien
// ---------------------------------------------------------------------------

export interface CategoryDto {
  id: string;
  name: string;
  color: string;
  icon: string;
  position: number;
}

export const createCategorySchema = z.object({
  name: nameSchema,
  color: hexColorSchema.optional(),
  icon: iconSchema.optional(),
});
export const patchCategorySchema = z.object({
  name: nameSchema.optional(),
  color: hexColorSchema.optional(),
  icon: iconSchema.optional(),
});
export const orderSchema = z.object({ ids: z.array(idSchema).max(500) });

// ---------------------------------------------------------------------------
// Playlists
// ---------------------------------------------------------------------------

export interface PlaylistDto {
  id: string;
  name: string;
  icon: string;
  accentColor: string;
  position: number;
  currentIndex: number;
  items: string[];
  updatedAt: number;
}

export interface PlaylistsState {
  activeId: string | null;
  playlists: PlaylistDto[];
}

export const createPlaylistSchema = z.object({
  name: nameSchema.optional(),
  icon: iconSchema.optional(),
  accentColor: hexColorSchema.optional(),
  documentIds: z.array(idSchema).max(1000).optional(),
});
export const patchPlaylistSchema = z.object({
  name: nameSchema.optional(),
  icon: iconSchema.optional(),
  accentColor: hexColorSchema.optional(),
});
export const addPlaylistItemsSchema = z.object({
  documentIds: z.array(idSchema).min(1).max(1000),
  position: z.number().int().min(0).optional(),
});
export const putPlaylistItemsSchema = z.object({
  documentIds: z.array(idSchema).max(1000),
  currentIndex: z.number().int().min(-1).optional(),
});
export const setCurrentIndexSchema = z.object({ index: z.number().int().min(-1) });
export const setActivePlaylistSchema = z.object({ id: idSchema });

// ---------------------------------------------------------------------------
// Annotationen
// ---------------------------------------------------------------------------

export interface AnnotationsDto {
  version: number;
  document: StrokeDocument;
}

// ---------------------------------------------------------------------------
// Einstellungen
// ---------------------------------------------------------------------------

export type UserSettingsDto = UserSettings;

// ---------------------------------------------------------------------------
// Statistik
// ---------------------------------------------------------------------------

export const pageTypeSchema = z.enum(["library", "viewer", "playlists", "admin", "login"]);
export type PageType = z.infer<typeof pageTypeSchema>;
export const pageViewSchema = z.object({ pageType: pageTypeSchema });

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export interface AdminUserDto extends UserDto {
  isActive: boolean;
  createdAt: number;
  lastLoginAt: number | null;
  documentCount: number;
  storageBytes: number;
}

export const createUserSchema = z.object({
  email: emailSchema,
  displayName: nameSchema.optional(),
  password: passwordSchema,
  role: roleSchema.default("user"),
  isActive: z.boolean().default(true),
});

export const patchUserSchema = z.object({
  displayName: nameSchema.optional(),
  role: roleSchema.optional(),
  isActive: z.boolean().optional(),
  password: passwordSchema.optional(),
});

export interface AdminStatsDto {
  users: { total: number; active: number; admins: number };
  documents: { total: number; storageBytes: number; missing: number; invalid: number };
  pageViews: { last24h: number; last7d: number; last30d: number; byType: Record<string, number> };
}

export interface PageViewEntryDto {
  id: number;
  pageType: string;
  userDisplayName: string | null;
  createdAt: number;
}

export interface RescanResultDto {
  added: number;
  updated: number;
  missing: number;
  restored: number;
  renamed: number;
}

// ---------------------------------------------------------------------------
// Live-Events (SSE)
// ---------------------------------------------------------------------------

export type ServerEvent =
  | { type: "presence"; originClientId?: string }
  | { type: "playlists"; originClientId?: string }
  | { type: "library"; originClientId?: string }
  | { type: "categories"; originClientId?: string }
  | { type: "document-prefs"; documentId: string; originClientId?: string }
  | { type: "settings"; originClientId?: string }
  | { type: "hue"; originClientId?: string }
  | { type: "session-revoked" };

// ---------------------------------------------------------------------------
// Anwesenheit (Live-Session: welches Gerät spielt gerade was)
// ---------------------------------------------------------------------------

export const putPresenceSchema = z.object({
  device: z.string().trim().min(1).max(40),
  documentId: idSchema.nullable(),
  playlistId: idSchema.nullable(),
  index: z.number().int().min(0).nullable(),
});
export type PutPresence = z.infer<typeof putPresenceSchema>;

export interface PresenceDto extends PutPresence {
  clientId: string;
  updatedAt: number;
}

export const CLIENT_ID_HEADER = "x-client-id";
export const CSRF_HEADER = "x-csrf-token";
export const UPLOAD_NAME_HEADER = "x-upload-name";
export const UPLOAD_META_HEADER = "x-upload-meta";
